import { Link } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import ScrollReveal from "@/components/base/ScrollReveal";
import SectionHeading from "@/components/base/SectionHeading";
import { GROUP_COMPANIES } from "@/constants/site";

export default function GroupPage() {
  return (
    <div className="w-full bg-background-50">
      <PageHero en="GROUP" ja="グループ企業" crumbs={[{ label: "グループ企業" }]} />

      <section className="w-full py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <div>
              <SectionHeading label="グループ企業" en="Our Group" />
              <div className="mt-8 flex max-w-2xl flex-col gap-6 font-body text-[15px] leading-[2.1] text-foreground-700">
                <p>
                  REVRA Groupでは、住宅設備・リフォーム、通信サービス、飲食・日本茶と、
                  暮らしに身近な領域でグループ各社が事業を展開しています。
                </p>
                <p>
                  各社に共通するのは、お客様一人ひとりに向き合う「ヒト」の力。
                  グループで培った営業力・人材育成のノウハウを活かし、それぞれの現場で価値を届けています。
                </p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={80}>
            <ul className="mt-14 grid grid-cols-1 gap-px border border-background-300/70 bg-background-300/70 sm:grid-cols-3">
              {GROUP_COMPANIES.map((company, index) => (
                <li key={company.name} className="flex items-center gap-4 bg-background-50 px-6 py-5">
                  <span className="font-label text-sm tracking-[0.2em] text-accent-600">
                    0{index + 1}
                  </span>
                  <span className="flex flex-col">
                    <span className="font-heading text-base tracking-[0.04em] text-foreground-950">
                      {company.name}
                    </span>
                    <span className="font-body text-xs text-foreground-600">{company.field}</span>
                  </span>
                </li>
              ))}
            </ul>
          </ScrollReveal>

          <div className="mt-16 flex flex-col gap-10 md:gap-12">
            {GROUP_COMPANIES.map((company, index) => {
              const image = (
                <img
                  src={company.image}
                  alt={`${company.name} イメージ`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              );
              return (
                <ScrollReveal key={company.name} delay={index * 80}>
                  <article
                    className={`group grid grid-cols-1 items-stretch overflow-hidden border border-background-200 bg-background-50 shadow-sm transition-shadow duration-300 hover:shadow-lg lg:grid-cols-2 ${
                      index % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    {company.url ? (
                      <a
                        href={company.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block h-56 w-full overflow-hidden bg-background-200 md:h-80"
                      >
                        {image}
                      </a>
                    ) : (
                      <div className="h-56 w-full overflow-hidden bg-background-200 md:h-80">
                        {image}
                      </div>
                    )}
                    <div className="flex flex-col justify-center p-7 md:p-12">
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="font-label text-xs tracking-[0.24em] text-secondary-500">
                          0{index + 1}
                        </span>
                        <span className="font-label text-[11px] tracking-[0.28em] text-secondary-500">
                          {company.en.toUpperCase()}
                        </span>
                        <span className="border border-background-300 px-2.5 py-1 font-body text-[11px] tracking-[0.1em] text-foreground-700">
                          {company.field}
                        </span>
                      </div>
                      <h2 className="mt-5 font-heading text-2xl font-medium tracking-[0.06em] text-foreground-950 md:text-3xl">
                        {company.name}
                      </h2>
                      {company.tagline ? (
                        <p className="mt-3 font-heading text-lg tracking-[0.06em] text-accent-700">
                          {company.tagline}
                        </p>
                      ) : null}
                      <p className="mt-5 max-w-lg font-body text-[15px] leading-[2.1] text-foreground-700">
                        {company.desc}
                      </p>
                      {company.url ? (
                        <a
                          href={company.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-8 inline-flex w-fit items-center gap-3 whitespace-nowrap border border-foreground-950 px-8 py-3 font-body text-[12px] tracking-[0.2em] text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
                        >
                          VISIT SITE
                          <i className="ri-arrow-right-up-line" />
                        </a>
                      ) : (
                        <span className="mt-8 inline-flex w-fit items-center gap-3 whitespace-nowrap border border-background-300 px-8 py-3 font-body text-[12px] tracking-[0.2em] text-secondary-500">
                          公式サイト準備中
                        </span>
                      )}
                    </div>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <p className="font-heading text-xl leading-[1.9] tracking-[0.04em] text-foreground-950 md:text-2xl">
                グループで、ヒトの可能性を価値に変える。
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/recruit"
                  className="inline-flex items-center justify-center gap-3 whitespace-nowrap bg-foreground-950 px-9 py-3.5 font-body text-[12px] tracking-[0.22em] text-background-50 transition-colors hover:bg-accent-600"
                >
                  採用情報
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 whitespace-nowrap border border-foreground-950 px-9 py-3.5 font-body text-[12px] tracking-[0.22em] text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
                >
                  お問い合わせ
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
